import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ConfigurationSection from "../../components/payments/ConfigurationSection/ConfigurationSection";
import { PaymentConfiguration } from "../../types/payments";
import { round2 } from "../../utils/money";
import { outlinedFieldSx } from "../../utils/fieldStyles";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

const commissionSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary
};

export interface FiscalOption {
  label: string;
  value: number;
}

/**
 * Who is charging, under which tax number, and where the request is sent.
 */
export default function PaymentDetailsSection({
  configurations,
  configId,
  onConfigIdChange,
  configuration,
  fiscalOptions,
  fiscalOption,
  onFiscalOptionChange,
  email,
  onEmailChange,
  emailValid
}: {
  configurations: PaymentConfiguration[];
  configId: string;
  onConfigIdChange: (configId: string) => void;
  configuration?: PaymentConfiguration;
  fiscalOptions: FiscalOption[];
  fiscalOption: number | "";
  onFiscalOptionChange: (option: number) => void;
  email: string;
  onEmailChange: (email: string) => void;
  emailValid: boolean;
}) {
  const { t } = useTranslation();
  const emailInError = email.length > 0 && !emailValid;

  return (
    <ConfigurationSection>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={7}>
          <TextField
            select
            label={t("requestPayment.billingEntity")}
            placeholder={t("requestPayment.billingEntityPlaceholder")}
            value={configId}
            onChange={event => onConfigIdChange(event.target.value)}
            fullWidth
            sx={outlinedFieldSx}
          >
            {configurations.map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.organizationName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {configuration && (
          <Grid item xs={12}>
            <Typography sx={commissionSx}>
              {/* commission is a fraction on the configuration, shown as a percentage. */}
              {t("requestPayment.commission", {
                value: round2(configuration.commission * 100)
              })}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} sm={5}>
          <TextField
            select
            label={t("requestPayment.fiscalId")}
            placeholder={t("requestPayment.fiscalIdPlaceholder")}
            value={fiscalOption}
            onChange={event => onFiscalOptionChange(Number(event.target.value))}
            fullWidth
            sx={outlinedFieldSx}
          >
            {fiscalOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            label={t("requestPayment.email")}
            value={email}
            onChange={event => onEmailChange(event.target.value)}
            error={emailInError}
            helperText={emailInError ? t("requestPayment.invalidEmail") : ""}
            fullWidth
            sx={outlinedFieldSx}
          />
        </Grid>
      </Grid>
    </ConfigurationSection>
  );
}
